/* istanbul ignore file */
import { APPLICATION_TYPE, APP_ID } from '@web/common/config'
import TagManager from '@web/services/Analytics/modules/TagManager'
import snakeCase from 'lodash.snakecase'
import { initClickEvent } from './utils'
import isEmpty from 'lodash.isempty'
import { SENSITIVE_URL_PARAMS, withoutQueryParamsDeep } from '@web/common/url'

/**
 * В события попадают адреса: target_url/banner_url баннеров, data-analytics-*
 * ссылок (админка — с ?token=). Токен и идентичность посетителя из них
 * вырезаем, остальной адрес не трогаем (MOB-586).
 */
function withoutSensitiveParams (event: EventBase): EventBase {
  const result: EventBase = { ...event }
  Object.keys(result).forEach(key => {
    const value = result[key]
    if (typeof value === 'string' && value.includes('?')) {
      result[key] = withoutQueryParamsDeep(value, SENSITIVE_URL_PARAMS)
    }
  })
  return result
}

class Analytics {
  static installed = false
  private static userId?: string | null
  private static trafficType: 'external' | 'internal' = 'external'
  private static userSegments: Segment[] = []
  private static superSub = false
  private static countActiveSubscriptions = 0
  private static typelk?: AnalyticsTypeLk | null
  private static courseIds?: string

  private constructor () {
    if (Analytics.installed) return
    Analytics.installed = true

    TagManager.init({
      userId: Analytics.userId,
      typelk: Analytics.typelk
    })

    initClickEvent()
  }

  static init ({ userId, typelk }: { userId?: number, typelk?: AnalyticsTypeLk }): void {
    Analytics.userId = !userId || userId === null ? '' : String(userId)
    Analytics.typelk = typelk

    // eslint-disable-next-line no-new
    new Analytics()
  }

  private static pageData: Dictionary<string> = {}

  static setPageData (payload: Dictionary<unknown>): void {
    for (const key in payload) {
      Analytics.pageData[snakeCase(key)] = String(payload[key])
    }
  }

  static clearPageData (): void {
    Analytics.pageData = {}
  }

  static send (event: EventBase): void {
    const _event: EventBase = {
      traffic_type: Analytics.trafficType,
      user_segments: (Analytics.userSegments || []).map(segment => segment.name).join(', '),
      user_segments_ids: (Analytics.userSegments || []).map(segment => segment.id).join(', '),
      application_id: APP_ID,
      application_type: APPLICATION_TYPE,
      super_sub: Analytics.superSub ? 'true' : 'false',
      count_active_subscriptions: Analytics.countActiveSubscriptions.toString(),
      user_id: Analytics.userId || '',
      ...(Analytics.typelk ? { type_lk: Analytics.typelk } : {}),
      ...(Analytics.courseIds ? { course_ids: Analytics.courseIds } : {}),
      ...Analytics.pageData,
      ...event
    }

    TagManager.send(withoutSensitiveParams(_event) as EventBase)
  }

  static setUserParams ({ userId, typelk }: { userId?: number | null, typelk?: AnalyticsTypeLk | null }) : void {
    const _userId = !userId || userId === null ? undefined : String(userId)
    const _typelk = !_userId || !typelk || typelk === null ? undefined : typelk
    const config: {
      user_id?: string | null
      type_lk?: AnalyticsTypeLk | null
    } = {}

    if (Analytics.userId !== _userId) {
      config.user_id = _userId
      Analytics.userId = _userId
    }
    if (Analytics.typelk !== _typelk) {
      Analytics.typelk = _typelk
      config.type_lk = _typelk
    }
    if (!isEmpty(config)) {
      TagManager.setConfig(config)
    }
  }

  static setCourseIds (courseIds: number[]): void {
    const _courseIds = courseIds.join(',')
    if (Analytics.courseIds === _courseIds) return

    Analytics.courseIds = _courseIds
    TagManager.setConfig({ course_ids: _courseIds })
  }

  static setUserId (id: number | null): void {
    const _id = id === null ? '' : String(id)
    if (Analytics.userId === _id) return
    Analytics.userId = _id
    TagManager.setUserId(_id)
  }

  static setTrafficType (trafficType: 'external' | 'internal'): void {
    Analytics.trafficType = trafficType
  }

  static setClub (club: string | null): void {
    Analytics.setPageData({ club: club || '' })
  }

  static setUserSegments (segments: Segment[]): void {
    Analytics.userSegments = segments || []
  }

  static setSuperSub (superSub: boolean): void {
    Analytics.superSub = superSub
  }

  static setCountActiveSubscriptions (countActiveSubscriptions: number): void {
    Analytics.countActiveSubscriptions = countActiveSubscriptions
  }

  static setTypeLk (typelk?: AnalyticsTypeLk): void {
    if (Analytics.typelk === typelk) return
    Analytics.typelk = typelk
    TagManager.setTypeLk(typelk)
  }
}

export default Analytics
